import {
  CHANNEL_COLORS,
  Channel,
  ChannelList,
  type FeeVersion,
  FeeVersion as FeeVersionSchema,
  OkResponse,
  PackagingKitList,
  addDaysIso,
  fixedFeeRangesProblem,
  formatBps,
  formatIsoDate,
  isoDateInSaoPaulo,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { AffixInput, Field, ReadOnlyNote, Toggle } from '../../../components/Form';
import { Icon } from '../../../components/Icon';
import { Modal } from '../../../components/Overlays';
import { PageHead } from '../../../components/PageHead';
import { Alert, EmptyState, ErrorBox } from '../../../components/States';
import { useToast } from '../../../components/Toast';
import { api, newIdempotencyKey } from '../../../lib/api';
import { errorMessage } from '../../../lib/messages';
import { bpsInput, centavosInput, parseBps, parseCentavos } from '../../../lib/numbers';
import { useIsAdmin } from '../../../lib/session';
import { CalibrationCard } from './CalibrationCard';
import { FeeTester } from './FeeTester';
import { commissionSummary, versionBadgeClass, versionLabel, versionRows } from './versions';
import { Combobox } from '../../../components/Combobox';
import { kitOption } from '../../../components/pickers';

const channelsKey = ['channels'] as const;

function useInvalidate() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: channelsKey });
}

// ---- dialogs ------------------------------------------------------------------------------------

function NewChannelDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (c: Channel) => void;
}) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(CHANNEL_COLORS[0] ?? '#0F5D6B');
  const [commission, setCommission] = useState('');
  const [payment, setPayment] = useState('');
  const [fixedFee, setFixedFee] = useState('');
  const [touched, setTouched] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const create = useMutation({
    mutationFn: () => {
      const fee = parseCentavos(fixedFee) ?? 0;
      return api.post(
        '/channels',
        Channel,
        {
          name: name.trim(),
          color,
          firstVersion: {
            commissionBps: parseBps(commission) ?? 0,
            paymentFeeBps: parseBps(payment) ?? 0,
            fixedFees: fee > 0 ? [{ fromCentavos: 0, toCentavos: null, feeCentavos: fee }] : [],
          },
        },
        { idempotencyKey: key },
      );
    },
    onSuccess: onCreated,
  });
  const nameBad = touched && name.trim() === '';
  return (
    <Modal
      title="Novo canal"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={create.isPending}
            onClick={() => {
              setTouched(true);
              if (name.trim() !== '') create.mutate();
            }}
          >
            Criar canal
          </button>
        </>
      }
    >
      <Field id="nn" label="Nome" error={nameBad ? 'Dê um nome ao canal.' : null}>
        <input
          id="nn"
          autoComplete="off"
          className={nameBad ? 'input err' : 'input'}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex.: Elo7, Instagram, loja parceira"
        />
      </Field>
      <Field label="Cor">
        <div className="row wrap" style={{ gap: 8 }}>
          {CHANNEL_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Cor ${c}`}
              aria-pressed={color === c}
              onClick={() => setColor(c)}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: c,
                border: color === c ? '3px solid var(--ink)' : '1px solid var(--line)',
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      </Field>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(150px,100%),1fr))',
          gap: 12,
        }}
      >
        <Field id="nc-c" label="Comissão">
          <AffixInput id="nc-c" unit="%" value={commission} onChange={setCommission} />
        </Field>
        <Field id="nc-p" label="Taxa de pagamento">
          <AffixInput id="nc-p" unit="%" value={payment} onChange={setPayment} />
        </Field>
        <Field id="nc-f" label="Tarifa fixa">
          <AffixInput id="nc-f" prefix="R$" value={fixedFee} onChange={setFixedFee} />
        </Field>
      </div>
      <p className="xs muted">
        Começa como v1, valendo a partir de hoje. Faixas de preço e frete você ajusta depois.
      </p>
      {create.isError ? <Alert tone="err">{errorMessage(create.error)}</Alert> : null}
    </Modal>
  );
}

// ---- draft editor -------------------------------------------------------------------------------

interface RangeForm {
  from: string;
  to: string;
  fee: string;
}

function DraftCard({
  channel,
  draft,
  current,
  readOnly,
}: {
  channel: Channel;
  draft: FeeVersion;
  current: FeeVersion | null;
  readOnly: boolean;
}) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const [date, setDate] = useState(formatIsoDate(draft.effectiveFrom));
  const [commission, setCommission] = useState(bpsInput(draft.commissionBps));
  const [payment, setPayment] = useState(bpsInput(draft.paymentFeeBps));
  const [ranges, setRanges] = useState<RangeForm[]>(
    draft.fixedFees.map((r) => ({
      from: centavosInput(r.fromCentavos),
      to: r.toCentavos === null ? '' : centavosInput(r.toCentavos),
      fee: centavosInput(r.feeCentavos),
    })),
  );
  const [shipFrom, setShipFrom] = useState(
    draft.shippingFromCentavos === null ? '' : centavosInput(draft.shippingFromCentavos),
  );
  const [shipAvg, setShipAvg] = useState(centavosInput(draft.shippingAverageCentavos));
  const [dialog, setDialog] = useState<'schedule' | 'discard' | null>(null);

  const isoDate = (() => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date.trim());
    return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
  })();
  const dateOk =
    isoDate !== null && !Number.isNaN(Date.parse(isoDate)) && isoDate > isoDateInSaoPaulo();
  const parsedRanges = ranges.map((r) => ({
    fromCentavos: parseCentavos(r.from) ?? 0,
    toCentavos: r.to.trim() === '' ? null : parseCentavos(r.to),
    feeCentavos: parseCentavos(r.fee) ?? 0,
  }));
  const rangeProblem = fixedFeeRangesProblem(parsedRanges);
  const body = {
    ...(dateOk && isoDate ? { effectiveFrom: isoDate } : {}),
    commissionBps: parseBps(commission) ?? 0,
    paymentFeeBps: parseBps(payment) ?? 0,
    fixedFees: parsedRanges,
    shippingFromCentavos: shipFrom.trim() === '' ? null : parseCentavos(shipFrom),
    shippingAverageCentavos: parseCentavos(shipAvg) ?? 0,
  };

  const save = useMutation({
    mutationFn: () => api.patch(`/fee-versions/${draft.id}`, FeeVersionSchema, body),
    onSuccess: invalidate,
  });
  const schedule = useMutation({
    mutationFn: async () => {
      await api.patch(`/fee-versions/${draft.id}`, FeeVersionSchema, body);
      return api.post(`/fee-versions/${draft.id}/schedule`, FeeVersionSchema);
    },
    onSuccess: (v) => {
      invalidate();
      setDialog(null);
      toast(`v${v.number} agendada para ${formatIsoDate(v.effectiveFrom)}`);
    },
  });
  const discard = useMutation({
    mutationFn: () => api.delete(`/fee-versions/${draft.id}`, OkResponse),
    onSuccess: () => {
      invalidate();
      toast('Rascunho descartado');
    },
  });

  const changed = current !== null && (parseBps(commission) ?? 0) !== current.commissionBps;
  const error = save.error ?? schedule.error ?? discard.error;

  return (
    <section className="card col" style={{ gap: 12, borderColor: 'var(--co)', borderWidth: 2 }}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <h3>v{draft.number} · nova versão</h3>
        <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <label htmlFor="ef">Vale a partir de</label>
          <input
            id="ef"
            className={dateOk ? 'input' : 'input err'}
            style={{ width: 140, height: 40 }}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            placeholder="dd/mm/aaaa"
            disabled={readOnly}
          />
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(160px,100%),1fr))',
          gap: 12,
        }}
      >
        <Field
          id="cm"
          label="Comissão"
          help={changed && current ? `vigente: ${formatBps(current.commissionBps)}` : undefined}
        >
          <AffixInput
            id="cm"
            unit="% do preço"
            value={commission}
            onChange={setCommission}
            changed={changed}
            disabled={readOnly}
          />
        </Field>
        <Field id="pf" label="Taxa de pagamento">
          <AffixInput id="pf" unit="%" value={payment} onChange={setPayment} disabled={readOnly} />
        </Field>
      </div>
      <span className="lbl">Tarifa fixa por faixa de preço</span>
      <div className="card flat" style={{ borderRadius: 12 }}>
        <div className="gt">
          <div
            className="gtr head"
            style={{ gridTemplateColumns: '1fr 1fr 1fr 40px', padding: '8px 10px' }}
          >
            <span>De (R$)</span>
            <span>Até (R$)</span>
            <span>Tarifa (R$)</span>
            <span />
          </div>
          {ranges.map((r, i) => (
            <div
              key={i}
              className="gtr"
              style={{ gridTemplateColumns: '1fr 1fr 1fr 40px', padding: '6px 10px', gap: 8 }}
            >
              {(['from', 'to', 'fee'] as const).map((k) => (
                <input
                  key={k}
                  className="cell"
                  aria-label={k === 'from' ? 'De' : k === 'to' ? 'Até' : 'Tarifa'}
                  value={r[k]}
                  placeholder={k === 'to' ? 'sem limite' : undefined}
                  style={k === 'fee' ? { textAlign: 'right' } : undefined}
                  onChange={(e) =>
                    setRanges(ranges.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))
                  }
                  disabled={readOnly}
                />
              ))}
              {!readOnly ? (
                <button
                  type="button"
                  className="btn sm g icon"
                  style={{ width: 36 }}
                  aria-label="Remover faixa"
                  onClick={() => setRanges(ranges.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              ) : null}
            </div>
          ))}
          {ranges.length === 0 ? (
            <p className="sm muted" style={{ padding: 12 }}>
              Sem tarifa fixa neste canal.
            </p>
          ) : null}
        </div>
      </div>
      {rangeProblem ? <span className="help err">{rangeProblem}</span> : null}
      {!readOnly ? (
        <button
          type="button"
          className="btn sm g"
          style={{ alignSelf: 'flex-start', padding: 0 }}
          onClick={() => {
            const last = parsedRanges.at(-1);
            const from = last ? (last.toCentavos ?? last.fromCentavos) + 1 : 0;
            setRanges([...ranges, { from: centavosInput(from), to: '', fee: '0,00' }]);
          }}
        >
          + Faixa
        </button>
      ) : null}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(160px,100%),1fr))',
          gap: 12,
        }}
      >
        <Field
          id="sf"
          label="Frete grátis a partir de"
          help="Vazio: você paga o frete em toda venda."
        >
          <AffixInput
            id="sf"
            prefix="R$"
            value={shipFrom}
            onChange={setShipFrom}
            placeholder="sem limite"
            disabled={readOnly}
          />
        </Field>
        <Field id="sa" label="Frete pago por você (média)" help="0 = o comprador paga.">
          <AffixInput
            id="sa"
            prefix="R$"
            value={shipAvg}
            onChange={setShipAvg}
            disabled={readOnly}
          />
        </Field>
      </div>
      {error ? <Alert tone="err">{errorMessage(error)}</Alert> : null}
      {!readOnly ? (
        <div className="row wrap" style={{ gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={() => setDialog('discard')}>
            Descartar rascunho
          </button>
          <button
            type="button"
            className="btn"
            disabled={!!rangeProblem || save.isPending}
            onClick={() => save.mutate(undefined, { onSuccess: () => toast('Rascunho salvo') })}
          >
            Salvar rascunho
          </button>
          <button
            type="button"
            className="btn p"
            disabled={!!rangeProblem || !dateOk}
            onClick={() => setDialog('schedule')}
          >
            Agendar v{draft.number}
          </button>
        </div>
      ) : (
        <p className="row sm muted" style={{ gap: 6, justifyContent: 'flex-end' }}>
          <Icon name="lock" size={14} />
          Só Admin altera e agenda versões.
        </p>
      )}
      {!dateOk && !readOnly ? (
        <span className="help err">Data inválida. Use dd/mm/aaaa, a partir de amanhã.</span>
      ) : null}

      {dialog === 'discard' ? (
        <Modal
          title={`Descartar o rascunho v${draft.number}?`}
          onClose={() => setDialog(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDialog(null)}>
                Cancelar
              </button>
              <button type="button" className="btn d" onClick={() => discard.mutate()}>
                Descartar
              </button>
            </>
          }
        >
          <p>
            As faixas e a comissão que você digitou somem.
            {current ? ` A v${current.number} continua valendo.` : ''}
          </p>
        </Modal>
      ) : null}
      {dialog === 'schedule' && isoDate ? (
        <Modal
          title={`Agendar v${draft.number} de ${channel.name}`}
          onClose={() => setDialog(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDialog(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn p"
                disabled={schedule.isPending}
                onClick={() => schedule.mutate()}
              >
                Agendar para {formatIsoDate(isoDate)}
              </button>
            </>
          }
        >
          <div className="kv">
            <span className="muted">Passa a valer em</span>
            <span className="v">{formatIsoDate(isoDate)}</span>
          </div>
          {current ? (
            <div className="kv">
              <span className="muted">Até {formatIsoDate(addDaysIso(isoDate, -1))}</span>
              <span className="v">v{current.number} continua</span>
            </div>
          ) : null}
          <div className="kv">
            <span className="muted">Comissão</span>
            <span className="v">
              {current ? `${formatBps(current.commissionBps)} → ` : ''}
              {formatBps(body.commissionBps)}
            </span>
          </div>
          <div className="kv">
            <span className="muted">Faixas de tarifa fixa</span>
            <span className="v">
              {current ? `${current.fixedFees.length} → ` : ''}
              {parsedRanges.length}
            </span>
          </div>
          <p className="sm muted">
            Preços sugeridos e o orçamento rápido mudam sozinhos nesse dia. Pedidos já feitos não
            mudam.
          </p>
          {schedule.isError ? <Alert tone="err">{errorMessage(schedule.error)}</Alert> : null}
        </Modal>
      ) : null}
    </section>
  );
}

function ScheduledCard({
  version,
  current,
  readOnly,
}: {
  version: FeeVersion;
  current: FeeVersion | null;
  readOnly: boolean;
}) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const unschedule = useMutation({
    mutationFn: () => api.post(`/fee-versions/${version.id}/unschedule`, FeeVersionSchema),
    onSuccess: () => {
      invalidate();
      setConfirm(false);
      toast(`Agendamento cancelado. A v${version.number} voltou a ser rascunho.`);
    },
  });
  return (
    <section className="card col" style={{ gap: 12, borderColor: 'var(--info)', borderWidth: 2 }}>
      <div className="row between wrap">
        <h3>v{version.number} · agendada</h3>
        <span className="badge info">vale a partir de {formatIsoDate(version.effectiveFrom)}</span>
      </div>
      {versionRows(version).map(([k, v]) => (
        <div key={k} className="kv">
          <span className="muted">{k}</span>
          <span className="v">{v}</span>
        </div>
      ))}
      {current ? (
        <p className="xs muted">
          Pedidos até {formatIsoDate(addDaysIso(version.effectiveFrom, -1))} continuam com a v
          {current.number}. Orçamentos que vencem depois disso mostram um aviso.
        </p>
      ) : null}
      {!readOnly ? (
        <button
          type="button"
          className="btn sm"
          style={{ alignSelf: 'flex-end' }}
          onClick={() => setConfirm(true)}
        >
          Cancelar agendamento
        </button>
      ) : null}
      {confirm ? (
        <Modal
          title="Cancelar o agendamento?"
          onClose={() => setConfirm(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirm(false)}>
                Manter agendada
              </button>
              <button type="button" className="btn d" onClick={() => unschedule.mutate()}>
                Cancelar agendamento
              </button>
            </>
          }
        >
          <p>
            A v{version.number} volta a ser rascunho e não entra em vigor em{' '}
            {formatIsoDate(version.effectiveFrom)}.
          </p>
          {unschedule.isError ? <Alert tone="err">{errorMessage(unschedule.error)}</Alert> : null}
        </Modal>
      ) : null}
    </section>
  );
}

// ---- categories and payment methods ---------------------------------------------------------

function CategoriesCard({ channel, readOnly }: { channel: Channel; readOnly: boolean }) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const [rows, setRows] = useState(
    channel.categories.map((c) => ({
      name: c.name,
      commission: c.commissionBps === null ? '' : bpsInput(c.commissionBps),
    })),
  );
  const dirty =
    JSON.stringify(rows) !==
    JSON.stringify(
      channel.categories.map((c) => ({
        name: c.name,
        commission: c.commissionBps === null ? '' : bpsInput(c.commissionBps),
      })),
    );
  const save = useMutation({
    mutationFn: () =>
      api.put(`/channels/${channel.id}/categories`, Channel, {
        categories: rows
          .filter((r) => r.name.trim() !== '')
          .map((r) => ({ name: r.name.trim(), commissionBps: parseBps(r.commission) })),
      }),
    onSuccess: () => {
      invalidate();
      toast('Categorias salvas');
    },
  });
  if (readOnly && rows.length === 0) return null;
  return (
    <section className="card col" style={{ gap: 12 }}>
      <div className="row between wrap">
        <h2>Comissão por categoria</h2>
        <span className="sm muted">
          Se a categoria do anúncio cobra diferente, vale o valor daqui.
        </span>
      </div>
      {rows.length > 0 ? (
        <div className="card flat" style={{ borderRadius: 12 }}>
          <div className="gt">
            <div className="gtr head" style={{ gridTemplateColumns: '1.4fr 160px 40px' }}>
              <span>Categoria</span>
              <span>Comissão</span>
              <span />
            </div>
            {rows.map((r, i) => (
              <div
                key={i}
                className="gtr"
                style={{ gridTemplateColumns: '1.4fr 160px 40px', padding: '8px 16px' }}
              >
                <input
                  className="cell"
                  aria-label="Categoria"
                  value={r.name}
                  placeholder="Nome da categoria no anúncio"
                  onChange={(e) =>
                    setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
                  }
                  disabled={readOnly}
                />
                <AffixInput
                  ariaLabel="Comissão da categoria"
                  unit="%"
                  value={r.commission}
                  placeholder="% da categoria"
                  onChange={(v) =>
                    setRows(rows.map((x, j) => (j === i ? { ...x, commission: v } : x)))
                  }
                  disabled={readOnly}
                />
                {!readOnly ? (
                  <button
                    type="button"
                    className="btn sm g icon"
                    style={{ width: 36 }}
                    aria-label="Remover categoria"
                    onClick={() => setRows(rows.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="row between wrap">
        {!readOnly ? (
          <button
            type="button"
            className="btn sm g"
            style={{ padding: 0 }}
            onClick={() => setRows([...rows, { name: '', commission: '' }])}
          >
            + Categoria
          </button>
        ) : (
          <span />
        )}
        <span className="xs muted">
          Vazio: usa a comissão da versão ({formatBps(channel.current?.commissionBps ?? 0)}).
        </span>
      </div>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      {dirty && !readOnly ? (
        <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar categorias
          </button>
        </div>
      ) : null}
    </section>
  );
}

function PaymentMethodsCard({ channel, readOnly }: { channel: Channel; readOnly: boolean }) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const initial = channel.paymentMethods.map((m) => ({
    name: m.name,
    fee: m.feeBps === null ? '' : bpsInput(m.feeBps),
  }));
  const [rows, setRows] = useState(initial);
  const dirty = JSON.stringify(rows) !== JSON.stringify(initial);
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/channels/${channel.id}`, Channel, {
        paymentMethods: rows
          .filter((r) => r.name.trim() !== '')
          .map((r) => ({ name: r.name.trim(), feeBps: parseBps(r.fee) })),
      }),
    onSuccess: () => {
      invalidate();
      toast('Taxas de pagamento salvas');
    },
  });
  if (readOnly && rows.length === 0) return null;
  return (
    <section className="card col" style={{ gap: 14 }}>
      <div className="row between wrap">
        <h2>Taxas de pagamento</h2>
        <span className="sm muted">maquininha e link de pagamento</span>
      </div>
      {rows.length === 0 ? (
        <p className="sm muted">
          Sem formas de pagamento. O preço usa a taxa de pagamento da versão (
          {formatBps(channel.current?.paymentFeeBps ?? 0)}).
        </p>
      ) : (
        <div className="card flat" style={{ borderRadius: 12 }}>
          <div className="gt">
            <div className="gtr head" style={{ gridTemplateColumns: '1.4fr 160px 40px' }}>
              <span>Forma de pagamento</span>
              <span>Taxa</span>
              <span />
            </div>
            {rows.map((r, i) => (
              <div
                key={i}
                className="gtr"
                style={{ gridTemplateColumns: '1.4fr 160px 40px', padding: '6px 16px' }}
              >
                <input
                  className="cell"
                  aria-label="Forma de pagamento"
                  value={r.name}
                  placeholder="Ex.: Crédito em 3×"
                  onChange={(e) =>
                    setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
                  }
                  disabled={readOnly}
                />
                <AffixInput
                  ariaLabel={`Taxa de ${r.name}`}
                  unit="%"
                  value={r.fee}
                  placeholder="taxa da maquininha"
                  onChange={(v) => setRows(rows.map((x, j) => (j === i ? { ...x, fee: v } : x)))}
                  disabled={readOnly}
                />
                {!readOnly ? (
                  <button
                    type="button"
                    className="btn sm g icon"
                    style={{ width: 36 }}
                    aria-label="Remover forma de pagamento"
                    onClick={() => setRows(rows.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
      {!readOnly ? (
        <div className="row wrap" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn sm g"
            style={{ padding: 0 }}
            onClick={() => setRows([...rows, { name: '', fee: '' }])}
          >
            + Forma de pagamento
          </button>
          {rows.length === 0 ? (
            <button
              type="button"
              className="btn sm g"
              style={{ padding: 0 }}
              onClick={() =>
                setRows([
                  { name: 'Pix', fee: '0' },
                  { name: 'Crédito à vista', fee: '' },
                  { name: 'Débito', fee: '' },
                ])
              }
            >
              Usar Pix, crédito e débito
            </button>
          ) : null}
        </div>
      ) : null}
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      {dirty && !readOnly ? (
        <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar taxas de pagamento
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** "Prazo de recebimento": only the cash view uses it; empty = counted on the sale date. */
function PayoutField({
  days,
  readOnly,
  onSave,
}: {
  days: number | null;
  readOnly: boolean;
  onSave: (v: number | null) => void;
}) {
  const [text, setText] = useState(days === null ? '' : String(days));
  const parsed = text.trim() === '' ? null : Number(text);
  const valid = parsed === null || (Number.isInteger(parsed) && parsed >= 0 && parsed <= 120);
  return (
    <div className="row wrap" style={{ gap: 10, alignItems: 'center' }}>
      <label className="lbl" htmlFor="payout-days">
        Prazo de recebimento
      </label>
      <div style={{ width: 130 }}>
        <AffixInput
          id="payout-days"
          value={text}
          onChange={setText}
          unit="dias"
          inputMode="numeric"
          placeholder="não sei"
          disabled={readOnly}
          invalid={!valid}
        />
      </div>
      {!readOnly && valid && parsed !== days ? (
        <button type="button" className="btn sm" onClick={() => onSave(parsed)}>
          Salvar
        </button>
      ) : null}
      <span className="xs muted">
        Dias da venda até o dinheiro cair. Deixe vazio se não souber: o caixa usa a data da venda.
      </span>
    </div>
  );
}

// ---- page ---------------------------------------------------------------------------------------

function ChannelDetail({ channel, readOnly }: { channel: Channel; readOnly: boolean }) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const [viewId, setViewId] = useState<string | null>(null);
  const kits = useQuery({
    queryKey: ['packaging-kits'],
    queryFn: () => api.get('/packaging-kits?limit=200', PackagingKitList),
  });
  const update = useMutation({
    mutationFn: (patch: {
      active?: boolean;
      showInQuickQuote?: boolean;
      defaultKitId?: string | null;
      payoutDays?: number | null;
    }) => api.patch(`/channels/${channel.id}`, Channel, patch),
    onSuccess: (c, patch) => {
      invalidate();
      if (patch.active === false) toast(`${c.name} desativado: some do orçamento rápido`);
      else if (patch.active === true) toast(`${c.name} ativado`);
      else if ('defaultKitId' in patch) toast(`Kit padrão de ${c.name} alterado`);
      else if ('payoutDays' in patch)
        toast(
          c.payoutDays === null
            ? `Prazo de recebimento de ${c.name} removido`
            : `${c.name}: dinheiro entra ${c.payoutDays} dias após a venda`,
        );
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const newDraft = useMutation({
    mutationFn: () =>
      api.post(
        `/channels/${channel.id}/versions`,
        FeeVersionSchema,
        {},
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: (v) => {
      invalidate();
      toast(`Rascunho v${v.number} criado a partir da v${channel.current?.number ?? v.number - 1}`);
    },
    onError: (err) => toast(errorMessage(err)),
  });

  const current = channel.current;
  const next = channel.versions.find((v) => v.state === 'draft' || v.state === 'scheduled') ?? null;
  const viewed =
    channel.versions.find(
      (v) => v.id === viewId && (v.state === 'current' || v.state === 'past'),
    ) ?? current;
  const nextNumber = (channel.versions[0]?.number ?? 0) + 1;

  return (
    <div className="col" style={{ flex: '4 1 560px', minWidth: 0, gap: 18 }}>
      <section className="card col" style={{ gap: 14 }}>
        <div className="row between wrap" style={{ gap: 12 }}>
          <div className="row" style={{ gap: 10 }}>
            <span className="dot" style={{ background: channel.color, width: 14, height: 14 }} />
            <h2>{channel.name}</h2>
            <span className={channel.active ? 'badge ok' : 'badge'}>
              {channel.active ? 'Ativo' : 'Inativo'}
            </span>
          </div>
          <div className="row wrap" style={{ gap: 16 }}>
            <span className="row sm" style={{ gap: 8 }}>
              <Toggle
                on={channel.active}
                label="Canal ativo"
                disabled={readOnly}
                onChange={(v) => update.mutate({ active: v })}
              />
              Ativo
            </span>
            <span className="row sm" style={{ gap: 8 }}>
              <Toggle
                on={channel.showInQuickQuote}
                label="Mostrar no orçamento rápido"
                disabled={readOnly}
                onChange={(v) => {
                  if (v && !channel.active) {
                    toast('Ative o canal antes');
                    return;
                  }
                  update.mutate({ showInQuickQuote: v });
                }}
              />
              No orçamento rápido
            </span>
            <Combobox
              ariaLabel="Kit padrão"
              style={{ width: 260, height: 40 }}
              value={channel.defaultKitId ?? ''}
              disabled={readOnly}
              options={[
                { value: '', label: 'Sem embalagem padrão', pinned: 'top' },
                ...(kits.data?.data ?? []).map(kitOption),
              ]}
              onChange={(v) => update.mutate({ defaultKitId: v === '' ? null : v })}
            />
          </div>
        </div>
        <PayoutField
          key={`${channel.id}-${channel.payoutDays}`}
          days={channel.payoutDays}
          readOnly={readOnly}
          onSave={(v) => update.mutate({ payoutDays: v })}
        />
        <div className="row wrap" style={{ gap: 8 }}>
          <span className="lbl" style={{ marginRight: 4 }}>
            Versões
          </span>
          {channel.versions.map((v) => (
            <button
              key={v.id}
              type="button"
              className={versionBadgeClass(v)}
              style={{
                border: 0,
                cursor: 'pointer',
                outline: viewed?.id === v.id ? '2px solid var(--pe)' : undefined,
              }}
              aria-pressed={viewed?.id === v.id}
              onClick={() => setViewId(v.id)}
            >
              {versionLabel(v)}
            </button>
          ))}
        </div>
      </section>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(320px,100%),1fr))',
          gap: 18,
        }}
      >
        {viewed ? (
          <section className="card col" style={{ gap: 12 }}>
            <div className="row between wrap">
              <h3>
                v{viewed.number} ·{' '}
                {viewed.state === 'current'
                  ? 'vigente'
                  : `encerrada em ${viewed.effectiveUntil ? formatIsoDate(viewed.effectiveUntil) : ''}`}
              </h3>
              <span className="xs muted">
                {viewed.state === 'current'
                  ? 'só leitura: pedidos guardam esta versão'
                  : 'só leitura: histórico'}
              </span>
            </div>
            {versionRows(viewed).map(([k, v]) => (
              <div key={k} className="kv">
                <span className="muted">{k}</span>
                <span className="v">{v}</span>
              </div>
            ))}
          </section>
        ) : null}
        {next?.state === 'draft' ? (
          <DraftCard
            key={next.id + next.createdAt}
            channel={channel}
            draft={next}
            current={current}
            readOnly={readOnly}
          />
        ) : null}
        {next?.state === 'scheduled' ? (
          <ScheduledCard version={next} current={current} readOnly={readOnly} />
        ) : null}
        {!next ? (
          <section className="card col" style={{ gap: 12, borderStyle: 'dashed' }}>
            <h3>Nenhuma mudança agendada</h3>
            <p className="sm muted">
              Quando o canal mudar as tarifas, crie uma versão nova com a data em que passa a valer.
              Pedidos antigos guardam a versão da época.
            </p>
            {!readOnly ? (
              <button
                type="button"
                className="btn"
                style={{ alignSelf: 'flex-start' }}
                disabled={newDraft.isPending}
                onClick={() => newDraft.mutate()}
              >
                Criar v{nextNumber}
              </button>
            ) : (
              <p className="row sm muted" style={{ gap: 6 }}>
                <Icon name="lock" size={14} />
                Só Admin altera
              </p>
            )}
          </section>
        ) : null}
      </div>

      <CategoriesCard
        key={`cat-${channel.id}-${channel.updatedAt}-${channel.categories.length}`}
        channel={channel}
        readOnly={readOnly}
      />
      <PaymentMethodsCard
        key={`pay-${channel.id}-${channel.updatedAt}`}
        channel={channel}
        readOnly={readOnly}
      />
    </div>
  );
}

/** Configurações · Canais (design Canais). */
export function ChannelsPage() {
  const isAdmin = useIsAdmin();
  const readOnly = !isAdmin;
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({
    queryKey: channelsKey,
    queryFn: () => api.get('/channels?limit=200', ChannelList),
  });
  const channels = list.data?.data ?? [];
  const selected = channels.find((c) => c.id === params.get('canal')) ?? channels[0] ?? null;

  return (
    <>
      <PageHead
        crumb="Configurações"
        title="Canais de venda"
        actions={
          readOnly ? (
            <div className="row" style={{ gap: 8 }}>
              <span className="badge info">Vendo como Operadora</span>
              <span className="badge">
                <Icon name="lock" size={14} />
                Só Admin altera
              </span>
            </div>
          ) : (
            <button type="button" className="btn" onClick={() => setCreating(true)}>
              + Novo canal
            </button>
          )
        }
      />
      {readOnly ? (
        <ReadOnlyNote>
          <b>Só Admin altera.</b> Você entrou como Operadora: pode ver as tarifas de cada canal e
          usar o “Testar tarifa”, mas não editar. Peça ao Admin se algo precisar mudar.
        </ReadOnlyNote>
      ) : null}
      {list.isPending ? (
        <div className="row" style={{ gap: 22, alignItems: 'flex-start' }} aria-busy="true">
          <div className="skel" style={{ width: 260, height: 300 }} />
          <div className="col grow" style={{ gap: 18 }}>
            <div className="skel" style={{ height: 110 }} />
            <div className="skel" style={{ height: 240 }} />
          </div>
        </div>
      ) : null}
      {list.isError ? (
        <div className="card">
          <ErrorBox
            title="Não foi possível carregar os canais"
            onRetry={() => void list.refetch()}
          />
        </div>
      ) : null}
      {list.isSuccess && channels.length === 0 ? (
        <div className="card">
          <EmptyState
            title="Nenhum canal ainda"
            action={
              !readOnly ? (
                <button type="button" className="btn p" onClick={() => setCreating(true)}>
                  + Novo canal
                </button>
              ) : null
            }
          >
            Cadastre onde você vende (Mercado Livre, Shopee, Pix, feira) para o orçamento calcular
            as tarifas de cada um.
          </EmptyState>
        </div>
      ) : null}
      {selected ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
            <nav
              className="card col"
              style={{ flex: '1 1 240px', gap: 2, padding: 8 }}
              aria-label="Canais"
            >
              {channels.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={c.id === selected.id ? 'navi on' : 'navi'}
                  style={{
                    border: 0,
                    background: c.id === selected.id ? undefined : 'transparent',
                    width: '100%',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit',
                    opacity: c.active ? 1 : 0.6,
                  }}
                  aria-current={c.id === selected.id ? 'true' : undefined}
                  onClick={() => setParams({ canal: c.id }, { replace: true })}
                >
                  <span className="dot" style={{ background: c.color }} />
                  <span className="trunc">{c.name}</span>
                  <span className="xs muted" style={{ marginLeft: 'auto' }}>
                    {c.active ? commissionSummary(c.current) : 'inativo'}
                  </span>
                </button>
              ))}
            </nav>
            <ChannelDetail key={selected.id} channel={selected} readOnly={readOnly} />
          </div>
          <FeeTester channels={channels} initialChannelId={selected.id} />
          <CalibrationCard
            onReview={(id) => {
              setParams({ canal: id }, { replace: true });
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </>
      ) : null}
      {creating ? (
        <NewChannelDialog
          onClose={() => setCreating(false)}
          onCreated={(c) => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: channelsKey });
            setParams({ canal: c.id }, { replace: true });
            toast(`Canal ${c.name} criado`);
          }}
        />
      ) : null}
    </>
  );
}
